"use client";

import { useRef, useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useTranslation } from "react-i18next";
import { ArrowRight, Volume2, VolumeX } from "lucide-react";
import ctaVideo from "@/assets/videos/cta_preview.mp4";
import { useSectionNavigation } from "./Header";
import { VideoWithShimmer } from "@/components/ui/video-with-shimmer";

function CallToActionSection() {
  const { t } = useTranslation();
  const { goToSection } = useSectionNavigation();
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );

  useEffect(() => {
    const check = () => setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useGSAP(() => {
    const ctx = gsap.context(() => {
      // Entrance for text content
      gsap.set(
        contentRef.current,
        { opacity: 0, y: 30 },
      );
      gsap.fromTo(
        contentRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 85%",
            end: "top 35%",
            scrub: isMobile ? false : 1, // Disable scrub on mobile
          },
        }
      );

      // Animation for video wrapper (reveal on scroll)
      if (videoWrapperRef.current) {
        gsap.fromTo(
          videoWrapperRef.current,
          { opacity: 0, y: isMobile ? 30 : 50, scale: isMobile ? 1 : 0.95 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: videoWrapperRef.current,
              start: "top 90%",
              end: "top 40%",
              scrub: isMobile ? false : 1.5, // Disable scrub on mobile
            },
          }
        );

      }
    }, sectionRef);
    return () => ctx.revert();
  }, [isMobile]); // Keep isMobile dependency to refresh animations on resize

  return (
    <section
      ref={sectionRef}
      className={cn(
        "relative py-12 px-4 overflow-visible",
        "md:py-32 md:px-12 md:mx-8",
        "2k:py-48 2k:px-24 2k:mx-12",
        "4k:py-64 4k:px-48 4k:mx-24"
      )}
    >
      <div ref={contentRef} className="relative z-10 mx-auto flex flex-col items-center text-center px-2 md:px-0 mb-6 md:mb-12">
        {/* Heading */}
        <h2 className={cn(
          "text-3xl font-bold mb-4 text-black",
          "md:text-5xl md:max-w-2xl",
          "2k:text-8xl 2k:mb-8 2k:max-w-6xl",
          "4k:text-[160px] 4k:mb-12 4k:max-w-8xl"
        )}>
          {t("hero.introParagraph")}
        </h2>

        {/* Subheading */}
        <p className={cn(
          "font-body text-black mb-10 max-w-xl",
          "text-lg md:text-2xl md:max-w-2xl",
          "2k:text-4xl 2k:mb-16 2k:max-w-6xl",
        )}>
          {t("hero.introBody")}
        </p>

        {/* CTA Button */}
        <Button
          size="lg"
          onClick={() => goToSection('#pricing')}
          className={cn(
            "group relative px-8 py-8 rounded-xl font-bold text-lg hover:scale-105 active:scale-95 transition-all duration-300",
            "bg-black text-white hover:opacity-90",
            "md:text-xl md:px-12",
            "2k:text-4xl 2k:px-20 2k:py-12 2k:rounded-2xl",
            "4k:text-6xl 4k:px-32 4k:py-20 4k:rounded-3xl"
          )}
        >
          {t("cta.button")}
          <ArrowRight className="ml-2 size-5 transition-all group-hover:translate-x-1 md:size-6 2k:size-10 4k:size-16" />
        </Button>
      </div>

      {/* Video Section with Spilled Cards */}
      <div 
        ref={videoWrapperRef}
        className="relative max-w-6xl mx-auto px-2 md:px-8"
      >
        {/* Main Video Container */}
        <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl">
          <VideoWithShimmer
            ref={videoRef}
            src={ctaVideo}
            autoPlay
            loop
            muted={isMuted}
            playsInline
            containerClassName="w-full h-auto aspect-video"
            className="w-full h-full object-cover"
          />

          {/* Mute Toggle - Hidden by User */}
          {/* <button
            onClick={() => setIsMuted(!isMuted)}
            className="absolute top-4 right-4 z-20 size-10 md:size-12 rounded-full bg-black/50 backdrop-blur-md text-white flex items-center justify-center hover:bg-black/70 transition-colors"
          >
            {isMuted ? <VolumeX className="size-5 md:size-6" /> : <Volume2 className="size-5 md:size-6" />}
          </button> */}
        </div>

      </div>
    </section>
  );
}

export { CallToActionSection };